import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatDialog,
  MatDialogModule,
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { firstValueFrom } from 'rxjs';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';
import {
  GroupCalendarLocationOption,
  GroupCalendarRotationMode,
  GroupCalendarSettings,
  GroupCalendarTemplateOption,
  WeeklyPlanDayMap,
} from './group-calendar-settings.interface';
import {
  applyGroupCalendarRotationToEvents,
  applyWeekRotationToEvents,
  clearDayGroupByDateForYear,
  eachDateKeyInYear,
  ensureWeekRotationConfigForYear,
  ensureWeekRotationWeeklyPlanLocation,
  ensureWeeklyPlanLocation,
  extractLocationOptions,
  fetchEventTemplates,
  getActiveRotationModeForYear,
  getWeekRotationConfigForYear,
  LOCATIONS_PAGE_SIZE,
  MAX_WEEK_ROTATION_WEEKS,
  normalizeWeekRotationWeeks,
  sortCalendarLocationGuids,
} from './group-calendar-settings.utils';

export interface GroupCalendarSettingsDialogData {
  settings: GroupCalendarSettings;
  groupsEnabled: boolean;
  groups: { group_id: number; name: string }[];
}

export interface PlanLocationOption {
  key: string;
  name: string;
}

const WEEKDAY_LABELS = [
  { key: '1', label: 'Mandag' },
  { key: '2', label: 'Tirsdag' },
  { key: '3', label: 'Onsdag' },
  { key: '4', label: 'Torsdag' },
  { key: '5', label: 'Fredag' },
  { key: '6', label: 'Lørdag' },
  { key: '7', label: 'Søndag' },
];

@Component({
  selector: 'app-group-calendar-settings-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatTooltipModule,
    MatTabsModule,
    MatButtonToggleModule,
  ],
  templateUrl: './group-calendar-settings-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './group-calendar-settings-dialog.component.scss',
})
export class GroupCalendarSettingsDialogComponent implements OnInit {
  locations: GroupCalendarLocationOption[] = [];
  filteredLocations: GroupCalendarLocationOption[] = [];
  templates: GroupCalendarTemplateOption[] = [];
  weekdayLabels = WEEKDAY_LABELS;
  maxWeekRotationWeeks = MAX_WEEK_ROTATION_WEEKS;
  localSettings: GroupCalendarSettings;
  locationSearch = '';
  loadingLocations = true;
  loadingTemplates = true;
  loadError: string | null = null;
  planLocationOptions: PlanLocationOption[] = [];
  groupRotationSequence: number[] = [];
  groupRotationAddId: number | null = null;
  rotationFeedback: string | null = null;
  applyingRotation = false;
  weekRotationFeedback: string | null = null;
  applyingWeekRotation = false;

  /** Active sub-mode under «Gruppefordeling» */
  rotationEditTab: GroupCalendarRotationMode = 'group';

  yearOptions: number[] = [];

  constructor(
    private dialogRef: MatDialogRef<
      GroupCalendarSettingsDialogComponent,
      GroupCalendarSettings | null
    >,
    @Inject(MAT_DIALOG_DATA) public data: GroupCalendarSettingsDialogData,
    private dataSourceService: ComponentDataSourceService,
    private dataDestService: ComponentDataDestService,
    private dialog: MatDialog
  ) {
    this.localSettings = structuredClone(data.settings);
    const baseYear =
      this.localSettings.weeklyPlanYear || new Date().getFullYear();
    this.yearOptions = [baseYear - 1, baseYear, baseYear + 1, baseYear + 2];
    this.rotationEditTab = this.resolveInitialRotationTab();
    this.refreshPlanLocationOptions();
    this.ensureAllPlanLocations();
    this.ensureAllWeekRotationPlans();
    this.loadGroupRotationSequenceForYear();
    this.ensureWeekRotationConfigLoaded();
  }

  ngOnInit(): void {
    void this.loadLocations();
    void this.loadTemplates();
  }

  get groupsEnabled(): boolean {
    return this.data.groupsEnabled;
  }

  get groups(): { group_id: number; name: string }[] {
    return this.data.groups ?? [];
  }

  get selectedLocationCount(): number {
    return this.localSettings.calendarLocationGuids.length;
  }

  get rotationPreview(): string {
    if (this.groupRotationSequence.length === 0) return '';
    return this.groupRotationSequence
      .map((id) => this.getGroupName(id))
      .join(' → ');
  }

  get weekRotationConfig() {
    return getWeekRotationConfigForYear(
      this.localSettings,
      this.localSettings.weekRotationYear
    );
  }

  get weekRotationWeekIndices(): number[] {
    const count = this.weekRotationConfig?.weekCount ?? 0;
    return Array.from({ length: count }, (_, i) => i);
  }

  get weekRotationPlanRowLabels(): { weekIndex: number; dayKey: string; label: string }[] {
    const cfg = this.weekRotationConfig;
    if (!cfg) return [];
    const rows: { weekIndex: number; dayKey: string; label: string }[] = [];
    for (let w = 0; w < cfg.weekCount; w++) {
      for (const wd of WEEKDAY_LABELS) {
        rows.push({
          weekIndex: w,
          dayKey: wd.key,
          label: `Uge ${w + 1} — ${wd.label}`,
        });
      }
    }
    return rows;
  }

  private resolveInitialRotationTab(): GroupCalendarRotationMode {
    if (this.localSettings.settingsRotationTab === 'week') return 'week';
    if (this.localSettings.settingsRotationTab === 'group') return 'group';
    const year = this.localSettings.groupRotationYear;
    return getActiveRotationModeForYear(this.localSettings, year);
  }

  onRotationEditTabChange(mode: GroupCalendarRotationMode): void {
    this.rotationEditTab = mode;
    this.localSettings.settingsRotationTab = mode;
    this.rotationFeedback = null;
    this.weekRotationFeedback = null;
  }

  private sortSelectedLocationGuids(): void {
    this.localSettings.calendarLocationGuids = sortCalendarLocationGuids(
      this.localSettings.calendarLocationGuids,
      this.locations
    );
  }

  private refreshPlanLocationOptions(): void {
    this.sortSelectedLocationGuids();
    const selected = this.localSettings.calendarLocationGuids.map((guid) => {
      const loc = this.locations.find((l) => l.guid === guid);
      return { key: guid, name: loc?.name ?? guid.slice(0, 8) };
    });
    this.planLocationOptions = [...selected, { key: 'other', name: 'Andet' }];
  }

  private ensureAllPlanLocations(): void {
    for (const loc of this.planLocationOptions) {
      ensureWeeklyPlanLocation(
        this.localSettings.weeklyPlanByYear,
        this.localSettings.weeklyPlanYear,
        loc.key
      );
    }
  }

  private ensureAllWeekRotationPlans(): void {
    const cfg = ensureWeekRotationConfigForYear(
      this.localSettings.weekRotationByYear,
      this.localSettings.weekRotationYear
    );
    for (const loc of this.planLocationOptions) {
      ensureWeekRotationWeeklyPlanLocation(
        this.localSettings.weekRotationWeeklyPlanByYear,
        this.localSettings.weekRotationYear,
        loc.key,
        cfg.weekCount
      );
    }
  }

  private ensureWeekRotationConfigLoaded(): void {
    ensureWeekRotationConfigForYear(
      this.localSettings.weekRotationByYear,
      this.localSettings.weekRotationYear
    );
  }

  private loadGroupRotationSequenceForYear(): void {
    const yearKey = String(this.localSettings.groupRotationYear);
    this.groupRotationSequence = [
      ...(this.localSettings.groupRotationByYear[yearKey] ?? []),
    ];
  }

  private persistGroupRotationSequenceToSettings(): void {
    const yearKey = String(this.localSettings.groupRotationYear);
    if (this.groupRotationSequence.length === 0) {
      delete this.localSettings.groupRotationByYear[yearKey];
      return;
    }
    this.localSettings.groupRotationByYear[yearKey] = [
      ...this.groupRotationSequence,
    ];
  }

  private persistWeekRotationConfigToSettings(): void {
    const yearKey = String(this.localSettings.weekRotationYear);
    const cfg = getWeekRotationConfigForYear(
      this.localSettings,
      this.localSettings.weekRotationYear
    );
    if (!cfg) {
      delete this.localSettings.weekRotationByYear[yearKey];
      return;
    }
    this.localSettings.weekRotationByYear[yearKey] = {
      weekCount: cfg.weekCount,
      anchorDate: cfg.anchorDate,
      weeks: cfg.weeks.map((w) => ({ ...w })),
    };
  }

  onGroupRotationYearChange(): void {
    this.persistGroupRotationSequenceToSettings();
    this.loadGroupRotationSequenceForYear();
    this.rotationFeedback = null;
  }

  onWeekRotationYearChange(): void {
    this.persistWeekRotationConfigToSettings();
    this.ensureWeekRotationConfigLoaded();
    this.ensureAllWeekRotationPlans();
    this.weekRotationFeedback = null;
  }

  onWeekCountChange(raw: number | string): void {
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    const weekCount = Math.max(
      1,
      Math.min(MAX_WEEK_ROTATION_WEEKS, Number.isFinite(n) ? n : 1)
    );
    const cfg = ensureWeekRotationConfigForYear(
      this.localSettings.weekRotationByYear,
      this.localSettings.weekRotationYear
    );
    cfg.weekCount = weekCount;
    cfg.weeks = normalizeWeekRotationWeeks(cfg.weeks, weekCount);
    this.ensureAllWeekRotationPlans();
    this.weekRotationFeedback = null;
  }

  onWeekRotationAnchorChange(anchorDate: string): void {
    const cfg = ensureWeekRotationConfigForYear(
      this.localSettings.weekRotationByYear,
      this.localSettings.weekRotationYear
    );
    cfg.anchorDate = anchorDate.slice(0, 10);
  }

  getWeekRotationGroup(weekIndex: number, dayKey: string): number[] {
    const cfg = this.weekRotationConfig;
    if (!cfg?.weeks[weekIndex]) return [];
    return cfg.weeks[weekIndex][dayKey] ?? [];
  }

  setWeekRotationGroup(
    weekIndex: number,
    dayKey: string,
    groupIds: number[]
  ): void {
    const cfg = ensureWeekRotationConfigForYear(
      this.localSettings.weekRotationByYear,
      this.localSettings.weekRotationYear
    );
    if (!cfg.weeks[weekIndex]) return;
    const ids = (Array.isArray(groupIds) ? groupIds : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    const unique = [...new Set(ids)];
    if (unique.length === 0) {
      delete cfg.weeks[weekIndex][dayKey];
    } else {
      cfg.weeks[weekIndex][dayKey] = unique;
    }
    this.weekRotationFeedback = null;
  }

  countWeekRotationAssignedCells(): number {
    const cfg = this.weekRotationConfig;
    if (!cfg) return 0;
    let count = 0;
    for (const week of cfg.weeks) {
      for (const key of ['1', '2', '3', '4', '5', '6', '7']) {
        if ((week[key]?.length ?? 0) > 0) count++;
      }
    }
    return count;
  }

  countWeekRotationEmptyCells(): number {
    const cfg = this.weekRotationConfig;
    if (!cfg) return 0;
    return cfg.weekCount * 7 - this.countWeekRotationAssignedCells();
  }

  getGroupName(groupId: number): string {
    return (
      this.groups.find((g) => g.group_id === groupId)?.name ??
      `Gruppe ${groupId}`
    );
  }

  addGroupToRotation(): void {
    if (this.groupRotationAddId == null || this.groupRotationAddId <= 0) return;
    this.groupRotationSequence.push(this.groupRotationAddId);
    this.groupRotationAddId = null;
    this.rotationFeedback = null;
  }

  removeGroupFromRotation(index: number): void {
    this.groupRotationSequence.splice(index, 1);
    this.rotationFeedback = null;
  }

  moveGroupInRotation(index: number, direction: -1 | 1): void {
    const target = index + direction;
    if (target < 0 || target >= this.groupRotationSequence.length) return;
    const seq = this.groupRotationSequence;
    [seq[index], seq[target]] = [seq[target], seq[index]];
    this.rotationFeedback = null;
  }

  clearGroupRotationSequence(): void {
    this.groupRotationSequence = [];
    this.rotationFeedback = null;
  }

  async applyGroupRotation(): Promise<void> {
    if (this.groupRotationSequence.length === 0) return;

    const year = this.localSettings.groupRotationYear;
    const dayCount = eachDateKeyInYear(year).length;
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmResetDialogComponent, {
          width: '520px',
          data: {
            title: 'Overskriv gruppevalg for hele året?',
            message: `Er du helt sikker? Alle gemte gruppevalg for ${year} erstattes af rotationsmønsteret nedenfor. Dette opdaterer events og kan ændre brugernes tildeling til øvelser.`,
            details: [
              `År: ${year}`,
              `Rækkefølge: ${this.rotationPreview}`,
              `Mønsteret gentages for alle ${dayCount} dage i året (f.eks. ${this.rotationPreview} …)`,
            ],
            confirmText: 'Ja, overskriv hele året',
            cancelText: 'Annuller',
            confirmColor: 'warn',
          },
        })
        .afterClosed()
    );

    if (!confirmed) return;

    this.persistGroupRotationSequenceToSettings();
    clearDayGroupByDateForYear(this.localSettings.dayGroupByDate, year);
    this.localSettings.activeRotationModeByYear[String(year)] = 'group';
    this.localSettings.settingsRotationTab = 'group';
    this.rotationEditTab = 'group';
    this.applyingRotation = true;
    this.rotationFeedback = null;
    try {
      const { updated } = await applyGroupCalendarRotationToEvents(
        this.dataDestService,
        year,
        this.groupRotationSequence
      );
      this.rotationFeedback =
        updated > 0
          ? `Rotation gemt og ${updated} event(s) opdateret for ${year}. Klik «Gem indstillinger» for at gemme øvrige indstillinger.`
          : `Rotation klar for ${year}. Klik «Gem indstillinger» for at gemme.`;
    } catch {
      this.rotationFeedback = 'Kunne ikke opdatere events — prøv igen.';
    } finally {
      this.applyingRotation = false;
    }
  }

  async applyWeekRotation(): Promise<void> {
    const cfg = this.weekRotationConfig;
    if (!cfg) return;

    const year = this.localSettings.weekRotationYear;
    const assigned = this.countWeekRotationAssignedCells();
    const empty = this.countWeekRotationEmptyCells();

    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmResetDialogComponent, {
          width: '520px',
          data: {
            title: 'Overskriv gruppevalg for hele året?',
            message: `Er du helt sikker? Alle gemte gruppevalg for ${year} erstattes af uge-rotationsmønsteret. Tomme celler fjerner gruppe på events. Dette kan ændre brugernes tildeling til øvelser.`,
            details: [
              `År: ${year}`,
              `Antal uger: ${cfg.weekCount}`,
              `Startdato (uge 1): ${cfg.anchorDate}`,
              `${assigned} tildelte dag-celler, ${empty} uden gruppe i mønsteret`,
            ],
            confirmText: 'Ja, overskriv hele året',
            cancelText: 'Annuller',
            confirmColor: 'warn',
          },
        })
        .afterClosed()
    );

    if (!confirmed) return;

    this.persistWeekRotationConfigToSettings();
    clearDayGroupByDateForYear(this.localSettings.dayGroupByDate, year);
    this.localSettings.activeRotationModeByYear[String(year)] = 'week';
    this.localSettings.settingsRotationTab = 'week';
    this.rotationEditTab = 'week';
    this.applyingWeekRotation = true;
    this.weekRotationFeedback = null;
    try {
      const { updated } = await applyWeekRotationToEvents(
        this.dataDestService,
        year,
        cfg
      );
      this.weekRotationFeedback =
        updated > 0
          ? `Uge-rotation gemt og ${updated} event(s) opdateret for ${year}. Klik «Gem indstillinger» for at gemme.`
          : `Uge-rotation klar for ${year}. Klik «Gem indstillinger» for at gemme.`;
    } catch {
      this.weekRotationFeedback = 'Kunne ikke opdatere events — prøv igen.';
    } finally {
      this.applyingWeekRotation = false;
    }
  }

  private async loadLocations(): Promise<void> {
    this.loadingLocations = true;
    this.loadError = null;
    try {
      const result = await firstValueFrom(
        this.dataSourceService.executeDataSource('locations', {
          filters: [
            { column: 'pageSize', value: LOCATIONS_PAGE_SIZE, operator: 'eq' },
            { column: 'pageIndex', value: 0, operator: 'eq' },
            // Only guid + name are used: skip per-user progress/validation on the server.
            { column: 'lightweight', value: 1, operator: 'eq' },
          ],
        })!
      );
      const items = result?.items ?? [];
      this.locations = extractLocationOptions(
        Array.isArray(items) ? items : []
      );
      this.applyLocationFilter();
      this.refreshPlanLocationOptions();
      this.ensureAllPlanLocations();
      this.ensureAllWeekRotationPlans();
      if (this.locations.length === 0) {
        this.loadError = 'Ingen lokationer fundet for organisationen.';
      }
    } catch {
      this.locations = [];
      this.filteredLocations = [];
      this.loadError = 'Kunne ikke hente lokationer.';
    } finally {
      this.loadingLocations = false;
    }
  }

  private async loadTemplates(): Promise<void> {
    this.loadingTemplates = true;
    try {
      this.templates = await fetchEventTemplates(this.dataSourceService);
    } catch {
      this.templates = [];
    } finally {
      this.loadingTemplates = false;
    }
  }

  applyLocationFilter(): void {
    const q = this.locationSearch.trim().toLowerCase();
    this.filteredLocations = q
      ? this.locations.filter((loc) => loc.name.toLowerCase().includes(q))
      : [...this.locations];
  }

  isLocationSelected(guid: string): boolean {
    return this.localSettings.calendarLocationGuids.includes(guid);
  }

  toggleLocation(guid: string, checked: boolean): void {
    const set = new Set(this.localSettings.calendarLocationGuids);
    if (checked) {
      set.add(guid);
      ensureWeeklyPlanLocation(
        this.localSettings.weeklyPlanByYear,
        this.localSettings.weeklyPlanYear,
        guid
      );
    } else {
      set.delete(guid);
    }
    this.localSettings.calendarLocationGuids = sortCalendarLocationGuids(
      Array.from(set),
      this.locations
    );
    this.refreshPlanLocationOptions();
    this.ensureAllPlanLocations();
    this.ensureAllWeekRotationPlans();
  }

  selectAllVisibleLocations(): void {
    const set = new Set(this.localSettings.calendarLocationGuids);
    for (const loc of this.filteredLocations) {
      set.add(loc.guid);
      ensureWeeklyPlanLocation(
        this.localSettings.weeklyPlanByYear,
        this.localSettings.weeklyPlanYear,
        loc.guid
      );
    }
    this.localSettings.calendarLocationGuids = sortCalendarLocationGuids(
      Array.from(set),
      this.locations
    );
    this.refreshPlanLocationOptions();
    this.ensureAllPlanLocations();
    this.ensureAllWeekRotationPlans();
  }

  clearAllLocations(): void {
    this.localSettings.calendarLocationGuids = [];
    this.refreshPlanLocationOptions();
  }

  onPlanYearChange(): void {
    this.ensureAllPlanLocations();
  }

  onWeekPlanYearChange(): void {
    this.ensureWeekRotationConfigLoaded();
    this.ensureAllWeekRotationPlans();
  }

  getTemplateColor(templateId: number): string {
    return this.localSettings.templateColors[String(templateId)] || '#94a3b8';
  }

  setTemplateColor(templateId: number, color: string): void {
    this.localSettings.templateColors[String(templateId)] = color;
  }

  getTemplateName(templateId: number | null): string {
    if (templateId == null) return '—';
    return (
      this.templates.find((tpl) => tpl.template_id === templateId)?.name ??
      'Skabelon'
    );
  }

  private readWeeklyPlanForLocation(locationKey: string): WeeklyPlanDayMap {
    const yearKey = String(this.localSettings.weeklyPlanYear);
    return this.localSettings.weeklyPlanByYear[yearKey]?.[locationKey] ?? {};
  }

  getWeeklyPlanDayForLocation(
    locationKey: string,
    dayKey: string
  ): number | null {
    return this.readWeeklyPlanForLocation(locationKey)[dayKey] ?? null;
  }

  setWeeklyPlanDayForLocation(
    locationKey: string,
    dayKey: string,
    templateId: number | null
  ): void {
    const plan = ensureWeeklyPlanLocation(
      this.localSettings.weeklyPlanByYear,
      this.localSettings.weeklyPlanYear,
      locationKey
    );
    if (templateId == null) {
      delete plan[dayKey];
    } else {
      plan[dayKey] = templateId;
    }
  }

  getWeekRotationWeeklyPlanTemplate(
    locationKey: string,
    weekIndex: number,
    dayKey: string
  ): number | null {
    const yearKey = String(this.localSettings.weekRotationYear);
    const plan =
      this.localSettings.weekRotationWeeklyPlanByYear[yearKey]?.[locationKey]?.[
        weekIndex
      ];
    if (!plan) return null;
    const tplId = plan[dayKey];
    return tplId != null && tplId > 0 ? tplId : null;
  }

  setWeekRotationWeeklyPlanTemplate(
    locationKey: string,
    weekIndex: number,
    dayKey: string,
    templateId: number | null
  ): void {
    const cfg = ensureWeekRotationConfigForYear(
      this.localSettings.weekRotationByYear,
      this.localSettings.weekRotationYear
    );
    const plans = ensureWeekRotationWeeklyPlanLocation(
      this.localSettings.weekRotationWeeklyPlanByYear,
      this.localSettings.weekRotationYear,
      locationKey,
      cfg.weekCount
    );
    const plan = plans[weekIndex] ?? {};
    if (templateId == null) {
      delete plan[dayKey];
    } else {
      plan[dayKey] = templateId;
    }
    plans[weekIndex] = plan;
  }

  save(): void {
    this.sortSelectedLocationGuids();
    this.persistGroupRotationSequenceToSettings();
    this.persistWeekRotationConfigToSettings();
    this.localSettings.settingsRotationTab = this.rotationEditTab;
    this.dialogRef.close(structuredClone(this.localSettings));
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
